import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './UserModal.css'; // 기존 모달 스타일 재사용

/**
 * 범용 확인 모달 컴포넌트
 * 삭제, 작업 확인 등에 사용
 * React Portal을 사용하여 DOM 트리 최상위에 렌더링
 */
const ConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm,
  title = "확인",
  message,
  confirmText = "확인",
  cancelText = "취소",
  confirmButtonClass = "btn-delete", // 'btn-save', 'btn-delete', 'btn-cancel'
  icon = "⚠️",
  style = {}
}) => {
  // 드래그 방지를 위한 상태
  const [mouseDownOnOverlay, setMouseDownOnOverlay] = useState(false);

  // ESC 키 이벤트 핸들러
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown, { passive: false }); // preventDefault 사용으로 passive: false
      // 모달이 열릴 때 body 스크롤 방지
      document.body.style.overflow = 'hidden';
    }

    // 클린업 함수
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // 모달이 닫힐 때 body 스크롤 복원
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleOverlayMouseDown = (e) => {
    if (e.target === e.currentTarget) {
      setMouseDownOnOverlay(true);
    }
  };

  const handleOverlayMouseUp = (e) => {
    if (e.target === e.currentTarget && mouseDownOnOverlay) {
      onClose();
    }
    setMouseDownOnOverlay(false);
  };

  const handleOverlayClick = (e) => {
    // click 이벤트는 이제 사용하지 않지만, 기존 호환성을 위해 유지
    e.preventDefault();
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  // 모달이 닫혀있을 때는 아무것도 렌더링하지 않음
  if (!isOpen) return null;

  // Portal을 사용하여 document.body에 직접 렌더링
  const modalContent = (
    <div 
      className="user-modal-overlay confirm-modal-overlay" 
      style={{
        zIndex: 999999, // SlideSearchPanel(999998)보다 높은 z-index 설정
        ...style
      }}
      onMouseDown={handleOverlayMouseDown}
      onMouseUp={handleOverlayMouseUp}
      onClick={handleOverlayClick}
    >
      <div className="user-modal confirm-modal" style={{ 
        maxWidth: '500px', 
        width: '90vw',
        position: 'relative',
        zIndex: 10001
      }}>
        <div className="user-modal-header">
          <h3>
            <span style={{ marginRight: '8px' }}>{icon}</span>
            {title}
          </h3>
          <button onClick={onClose} className="user-modal-close">
            ✕
          </button>
        </div>
        
        <div className="user-modal-body" style={{ 
          padding: '30px 20px',
          textAlign: 'center'
        }}>
          <div style={{ 
            fontSize: '16px',
            lineHeight: '1.5',
            color: '#374151',
            whiteSpace: 'pre-line' // 줄바꿈 문자 처리
          }}>
            {message}
          </div>
        </div>

        <div className="user-modal-footer" style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '12px',
          padding: '20px',
          borderTop: '1px solid #e5e7eb'
        }}>
          <button 
            onClick={onClose}
            className="btn-cancel"
            style={{
              padding: '10px 20px',
              minWidth: '80px'
            }}
          >
            {cancelText}
          </button>
          <button 
            onClick={handleConfirm}
            className={confirmButtonClass}
            style={{
              padding: '10px 20px',
              minWidth: '80px'
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );

  // Portal을 사용하여 document.body에 렌더링
  return createPortal(modalContent, document.body);
};

export default ConfirmModal; 